<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    title="Create List"
    direction="right"
    width="500px"
    :id="`${source}_add_entity_list`"
  >
    <div class="flex flex-col gap-6 secondary_color text-[13px] font-medium">
      <div id="list_name" class="form_col">
        <span>List Name</span>

        <CustomInput
          format="text"
          v-model="newEntityList.name"
          placeholder="Enter list name"
          type="primary"
          auto-focus
        />
      </div>

      <div class="form_col">
        <span>Object</span>

        <CustomSelector
          source="add_entity_list"
          :options="filteredEntitySelectorOptions"
          v-model="newEntityList.entityType"
        />
      </div>

      <div class="form_col">
        <span>Who can access this list</span>
        <CustomSelector
          source="add_entity_list"
          :options="listAccessSelectorOptions"
          v-model="newEntityList.accessType"
        />
      </div>

      <div v-if="shouldShowUserSelect" class="form_col">
        <span>Members who can access</span>
        <a-select
          v-model:value="selectedUserIds"
          mode="multiple"
          placeholder="Select members"
          show-search
          :show-arrow="false"
          :filter-option="filterOptions"
          :options="userOptions"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="onCancel"> Cancel </a-button>
        <a-button
          class="primary_medium"
          @click="createEntityList"
          :loading="isCreating"
          :disabled="shouldDisableCreate"
        >
          Create
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import { computed, ref, watch } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import CustomInput from "@/src/components/CustomInput.vue";
import CustomSelector from "@/src/components/CustomSelector.vue";
import {
  entitySelectorOptions,
  listAccessSelectorOptions,
  listAccessTypes,
} from "@/src/constants/listsConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useRouter } from "vue-router";
import { renderEntity } from "../../utils/utils.js";
import { useStore } from "vuex";
import { canView as canViewABAC } from "@/src/utils/abacPermissionUtils.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  drawerVisible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:modelValue"]);

const store = useStore();
const internalDrawerVisible = ref(props.drawerVisible);
const isCreating = ref(false);
const router = useRouter();
const users = ref([]);
const selectedUserIds = ref([]);

const isCrmListsEnabled = computed(
  () => localStorage.getItem("crm_lists_service") === "true"
);

const STANDARD_SLUG_MAP = {
  contacts: entityTypes.CONTACT,
  leads: entityTypes.LEAD,
  companies: entityTypes.COMPANY,
};

// Helper function to check VIEW access (works for both RBAC and ABAC)
const canViewEntity = (resourceSlug) => {
  if (isABACEnabled()) {
    return canViewABAC(resourceSlug);
  }
  // In RBAC mode, check the appropriate store getter
  if (resourceSlug === entityTypes.CONTACTS) {
    return store.getters["rbac/hasContactsAccess"];
  } else if (resourceSlug === entityTypes.COMPANIES) {
    return store.getters["rbac/hasCompaniesAccess"];
  } else if (resourceSlug === entityTypes.LEADS) {
    return store.getters["rbac/hasLeadsAccess"];
  }
  // Custom objects — allow access in RBAC mode (ABAC handled above)
  return true;
};

const filteredEntitySelectorOptions = computed(() => {
  const resourceSlugMap = {
    [entityTypes.CONTACT]: entityTypes.CONTACTS,
    [entityTypes.COMPANY]: entityTypes.COMPANIES,
    [entityTypes.LEAD]: entityTypes.LEADS,
  };
  return entitySelectorOptions.filter((option) => {
    const resourceSlug = resourceSlugMap[option.id];
    return canViewEntity(resourceSlug);
  });
});

const defaultEntityType = computed(() => {
  return filteredEntitySelectorOptions.value.length > 0
    ? filteredEntitySelectorOptions.value[0].id
    : entityTypes.CONTACT;
});

const newEntityList = ref({
  name: "",
  entityType: defaultEntityType.value,
  accessType: listAccessTypes.ANYONE,
});

const shouldShowUserSelect = computed(
  () => newEntityList?.value?.accessType === listAccessTypes.PRIVATE
);

const shouldDisableCreate = computed(
  () =>
    newEntityList?.value?.accessType === listAccessTypes.PRIVATE &&
    !selectedUserIds?.value?.length
);

const loggedInUserId = computed(() => {
  const storedEmail = localStorage.getItem("userEmail") || "";
  return users?.value?.find((user) => user?.email === storedEmail)?.id || null;
});

const userOptions = computed(() => {
  if (!users.value) return [];

  return users.value.map((user) => {
    const userName = renderEntity(user);

    return {
      value: user.id,
      label: userName,
      disabled: user.id === loggedInUserId.value,
    };
  });
});

const filterOptions = (input, option) => {
  try {
    if (!input?.trim()) return true;

    const user = users?.value?.find((user) => user?.id === option?.value);
    const name = user?.name?.trim() || "";

    return name.toLowerCase().includes(input.trim().toLowerCase());
  } catch (error) {
    console.error("Error in filterOptions:", error);
    return false;
  }
};

const onCancel = () => {
  emit("update:modelValue", false);
};

const createEntityList = async () => {
  if (!newEntityList.value.name) {
    message.error("Please enter a list name");
    return;
  }

  isCreating.value = true;

  try {
    const payload = {
      list_name: newEntityList.value.name,
      entity_type: newEntityList.value.entityType.toLowerCase(),
      private_flag: false,
    };

    if (newEntityList?.value?.accessType === listAccessTypes.PRIVATE) {
      payload.private_flag = true;
      payload.member_ids = selectedUserIds.value;
    }

    const response = await DashboardService.createEntityList(payload);

    if (response?.success) {
      await router.push(
        `/list/${response.entity_list}/${newEntityList.value.entityType}`
      );
      emit("update:modelValue", false);
      resetForm();
      message.success(`${payload.list_name} list created successfully`);
    }
  } catch (error) {
    console.error("Error creating entity list:", error);
    message.error("An error occurred while creating the entity list");
  } finally {
    isCreating.value = false;
  }
};

const fetchUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers();

    if (response?.success) {
      users.value = response?.users || [];
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const resetForm = () => {
  newEntityList.value = {
    name: "",
    entityType: defaultEntityType.value,
    accessType: listAccessTypes.ANYONE,
  };

  selectedUserIds.value = loggedInUserId.value ? [loggedInUserId.value] : [];
};

watch(
  () => props.drawerVisible,
  (newVal) => {
    internalDrawerVisible.value = newVal;

    if (newVal) {
      fetchUsers();
      store.dispatch("objects/fetchObjects");
    }
  }
);

watch(
  () => newEntityList?.value?.accessType,
  (newVal) => {
    if (newVal === listAccessTypes.PRIVATE) {
      selectedUserIds.value = loggedInUserId.value
        ? [loggedInUserId.value]
        : [];
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      resetForm();
      emit("update:modelValue", false);
    }
  }
);
</script>
